import PropTypes from "prop-types";
import React from "react";
import getClassNameFactory from "../../../../../utilities/classNames";
import TextCardBase from "../../bases/TextCardBase";
import { Rank } from "../../../../../core/types";
import Image from "../../../../../elements/Image";

var PairedCard = function PairedCard(_ref) {
  var borderless = _ref.borderless,
      eyebrow = _ref.eyebrow,
      description = _ref.description,
      secondaryText = _ref.secondaryText,
      footer = _ref.footer,
      imgAnchor = _ref.imgAnchor,
      imgSrc = _ref.imgSrc,
      imgSrcSet = _ref.imgSrcSet,
      heading = _ref.heading,
      headingRank = _ref.headingRank,
      href = _ref.href,
      id = _ref.id,
      tag = _ref.tag;
  var getClassName = getClassNameFactory(PairedCard.displayName);
  return /*#__PURE__*/React.createElement("div", {
    className: getClassName({
      modifiers: borderless ? "borderless" : ""
    })
  }, /*#__PURE__*/React.createElement(TextCardBase, {
    anchor: "top",
    borderless: true,
    description: description,
    secondaryText: secondaryText,
    eyebrow: eyebrow,
    footer: footer,
    heading: heading,
    headingRank: headingRank,
    href: href,
    size: "large",
    id: id,
    renderContent: function renderContent(content) {
      return /*#__PURE__*/React.createElement("div", {
        className: getClassName({
          descendantName: "content"
        })
      }, /*#__PURE__*/React.createElement("div", {
        className: getClassName({
          descendantName: "image"
        })
      }, /*#__PURE__*/React.createElement(Image, {
        alt: "",
        anchor: imgAnchor,
        fit: "cover" // Although paired cards are most likely to be used in a 1/3
        // grid item, this assumes the card is used in a 2/3 grid
        // column. We make this assumption as we can't identify the
        // context in which the card is used. This will slightly affect
        // performance, as larger images may be loaded than necessary.
        ,
        sizes: Image.sizes.twoThirds,
        src: imgSrc,
        srcSet: imgSrcSet
      })), /*#__PURE__*/React.createElement("div", {
        className: getClassName({
          descendantName: "contentInner"
        })
      }, tag && /*#__PURE__*/React.createElement("div", {
        className: getClassName({
          descendantName: "tagContainer"
        })
      }, tag), content));
    }
  }));
};

PairedCard.displayName = "PairedCard";
/* eslint-disable max-len */

PairedCard.propTypes = {
  /**
   * Hide the border
   */
  borderless: PropTypes.bool,

  /**
   * Description text
   */
  description: PropTypes.node,

  /**
   * Secondary text
   */
  secondaryText: PropTypes.node,

  /**
   * Eyebrow text
   */
  eyebrow: PropTypes.string,

  /**
   * Footer. Should be a valid card footer, like ArticleCardFooter.
   */
  footer: PropTypes.node.isRequired,

  /**
   * Heading text
   */
  heading: PropTypes.string.isRequired,

  /**
   * Heading rank. 1 will render `<h1 />`, and so on. Defaults to 3.
   */
  headingRank: PropTypes.oneOf(Object.values(Rank)),

  /**
   * Card URL
   */
  href: PropTypes.string.isRequired,

  /**
   * Anchor point for the image.
   */
  imgAnchor: PropTypes.oneOf(Object.values(Image.AnchorTypes)),

  /**
   * Image `src`. Used if srcSet is not defined, or as a fallback.
   */
  imgSrc: PropTypes.string.isRequired,

  /**
   * A comma-separated list indicating a set of possible image sources for the image. The image will be fit to cover. This is used by the user agent to determine the correct image for different screen sizes. Same format as [srcSet](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#Example_4_Using_the_srcset_and_sizes_attributes), i.e. `my-image-200.png 200w, my-image-200.png 200w`.
   */
  imgSrcSet: PropTypes.string,

  /**
   * id to identity component for Selenium Test
   */
  id: PropTypes.string,

  /**
   * tag string to show in the card
   */
  tag: PropTypes.string
};
/* eslint-enable max-len */

PairedCard.defaultProps = {
  headingRank: "3",
  imgGradient: false,
  imgAnchor: "C"
};
export default PairedCard;